<template>
  <div
    data-testid="ai-chat-consent-notice"
    class="rounded-md border p-3 text-sm transition-colors duration-300"
    :class="
      attention > 0
        ? 'border-purple-300 bg-purple-50'
        : 'border-gray-200 bg-gray-50'
    "
  >
    <template v-if="isManager">
      <p class="font-semibold text-gray-900">
        One step before your first question
      </p>
      <p class="mt-1 text-gray-600">
        Automagic AI needs a manager's consent for your organization. You'll
        review the terms when you send, and your question goes through as soon
        as you accept.
      </p>
      <button
        type="button"
        class="mt-2 font-medium text-purple-700 hover:text-purple-900"
        @click="emit('reviewTerms')"
      >
        Review terms
      </button>
    </template>

    <template v-else-if="isMember">
      <!-- Focus lands here, not on the button, when Send is refused: a second
        Enter would otherwise notify every manager without a deliberate
        choice. -->
      <p
        ref="heading"
        tabindex="-1"
        class="font-semibold text-gray-900 focus:outline-none"
      >
        A manager needs to enable Automagic AI
      </p>
      <!-- One live region that stays mounted, so the outcome is announced
        when its text changes; one inserted already filled often isn't. -->
      <p role="status" class="mt-1 text-gray-600">
        <template v-if="request.data === undefined">
          Your organization's managers must accept the AI terms before anyone
          can send questions. Ask them below and we'll let them know.
        </template>
        <template v-else-if="request.data.managerCount === 0">
          Your organization has no active manager to ask. Contact
          <a
            :href="`mailto:${SALES_EMAIL}`"
            class="font-medium text-purple-700"
          >
            {{ SALES_EMAIL }}
          </a>
          for help.
        </template>
        <template v-else-if="request.data.alreadyRequested">
          You already asked {{ requestedAgo }}. Your managers have been
          notified, and you can send as soon as one of them enables it.
        </template>
        <template v-else>
          Request sent to {{ request.data.managerCount }}
          {{ request.data.managerCount === 1 ? "manager" : "managers" }}. You
          can send as soon as one of them enables it.
        </template>
      </p>

      <button
        v-if="request.data === undefined"
        type="button"
        class="btn-primary mt-3"
        :disabled="request.isPending"
        @click="ask"
      >
        <Icon
          v-if="request.isPending"
          name="spinner-third-duotone"
          class="mr-2 size-4 animate-spin"
        />
        Ask a manager to enable
      </button>
      <p v-if="request.isError" role="alert" class="mt-2 text-red-600">
        {{ requestError }}
      </p>
    </template>

    <div v-else-if="roleFailed" role="alert">
      <p class="font-semibold text-gray-900">
        Automagic AI needs a manager's consent
      </p>
      <p class="mt-1 text-gray-600">
        We couldn't confirm your permissions, so we can't tell whether you can
        accept the terms or need to ask a manager.
      </p>
      <button
        type="button"
        class="btn btn-outline mt-3"
        @click="emit('retryRole')"
      >
        Retry
      </button>
    </div>

    <p v-else class="text-gray-600">
      Automagic AI needs a manager's consent before it can be used.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { DateTime } from "luxon";
import Icon from "@/components/Core/Icon.vue";
import { useRequestConsentMutation } from "@/services/consent";
import { SALES_EMAIL } from "@/services/paywall_content";
import { isOrganizationChangedError } from "@/services/queryRetry";
import { formatDrfRefusal } from "@/utils/drf-errors";

const props = defineProps({
  consentKey: { type: String, required: true },
  isManager: { type: Boolean, required: true },
  isMember: { type: Boolean, required: true },
  roleFailed: { type: Boolean, default: false },
  // Bumped each time the user tries to send, so every attempt moves focus
  // here rather than doing nothing visible.
  attention: { type: Number, default: 0 },
});

const emit = defineEmits({
  reviewTerms: () => true,
  retryRole: () => true,
});

const request = useRequestConsentMutation();
const heading = ref<HTMLElement | null>(null);

const requestedAgo = computed(() => {
  const at = request.data?.requestedAt;
  return at ? (DateTime.fromISO(at).toRelative() ?? "earlier") : "earlier";
});

const requestError = computed(() =>
  isOrganizationChangedError(request.error)
    ? "Your organization changed in another tab. Try again."
    : formatDrfRefusal(request.error, "Couldn't send the request. Try again."),
);

// The button is removed on success; without this focus drops to <body>.
function ask(): void {
  request.mutate(props.consentKey, { onSuccess: () => void focusNotice() });
}

async function focusNotice(): Promise<void> {
  await nextTick();
  heading.value?.focus();
}

watch(() => props.attention, focusNotice);
// A prompt handed over from elsewhere is refused during the panel's setup,
// before this notice exists to watch the bump.
onMounted(() => {
  if (props.attention > 0) void focusNotice();
});
</script>
